/**
 * CommissionEditModal — Chad edits a pending deal's commission, one row per
 * person per line (Sep 30, 2026 rewrite, Greg: "itemize it" + the amounts were
 * wrong).
 *
 * Rows come from the payout engine (commissionEditLines in CommissionBoard), so
 * each defaults to exactly what the board pays:
 *   • each RM at THEIR OWN rate on THEIR half of a split
 *   • TL 1% on the half(s) whose RM reports to them
 *   • ASM 4% of revenue EXCLUDING their own half when they're also an RM
 *   • (Oct 29, 2026) the trade upside pool — the homeowner's price above the
 *     30% floor on the homeowner-billed trades — to the RM(s) as "Upside" rows
 *     (half each on a split); TL / ASM Upside rows start at $0 so Chad can
 *     carve them a flat share, the retail rule
 * A % is always a % of that row's own base. Saving writes the changed rows to
 * deals.commission_line_overrides ({"<member_id>:rm|tl|asm|upside": gross $}) —
 * the key the engine reads — and clears the legacy whole-deal rm_commission.
 *
 * The old version wrote TL/ASM edits to commission_overrides, a table nothing
 * reads (they never changed a payout), priced every row at full revenue, and
 * saved one RM total that paid both split RMs the owner's rate. Its "all future
 * deals" option went to the same dead table and is gone — per-person rates live
 * on the Team Roster board.
 */

import { useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";
import type { CommissionDeal, CommissionEditLine } from "./CommissionBoard";

interface RowEdit {
  mode: "percent_rate" | "flat_amount";
  pct: string;  // % of the row's base, as typed
  flat: string; // $ as typed
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const keyOf = (l: CommissionEditLine) => `${l.personId}:${l.kind}`;
const ROLE_CLS: Record<string, string> = {
  RM: "bg-bardin-teal/10 text-bardin-teal",
  TL: "bg-amber-50 text-amber-700",
  ASM: "bg-purple-50 text-purple-700",
  Upside: "bg-teal-50 text-teal-700",
};

function initialEdit(l: CommissionEditLine): RowEdit {
  if (l.overridden || l.formulaRate <= 0) {
    return { mode: "flat_amount", pct: "", flat: l.currentGross ? l.currentGross.toFixed(2) : "" };
  }
  return { mode: "percent_rate", pct: String(+(l.formulaRate * 100).toFixed(3)), flat: "" };
}

export default function CommissionEditModal({
  deal,
  lines,
  onClose,
  onSaved,
}: {
  deal: CommissionDeal;
  lines: CommissionEditLine[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { member } = useAuth();
  const [edits, setEdits] = useState<Record<string, RowEdit>>(() =>
    Object.fromEntries(lines.map((l) => [keyOf(l), initialEdit(l)])),
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The job's revenue is the biggest base among the revenue rows; the Upside rows' base is the pool, not revenue.
  const revenue = lines.filter((l) => l.kind !== "upside").reduce((m, l) => Math.max(m, l.base), 0);
  const isRetail = deal.is_retail === true;
  // Oct 29, 2026 — the trade upside pool (insurance deals): what the Upside rows share.
  const upsidePool = lines.filter((l) => l.kind === "upside").reduce((m, l) => Math.max(m, l.pool ?? l.base), 0);
  const hasUpsideRows = lines.some((l) => l.kind === "upside");

  function grossFor(l: CommissionEditLine): number {
    const e = edits[keyOf(l)];
    if (!e) return l.currentGross;
    if (e.mode === "percent_rate") return round2(l.base * (Math.max(0, parseFloat(e.pct) || 0) / 100));
    return round2(Math.max(0, parseFloat(e.flat) || 0));
  }

  const rows = useMemo(
    () => lines.map((l) => {
      const gross = grossFor(l);
      const fees = l.fees.reduce((s, f) => s + f.amount, 0);
      return { l, gross, net: Math.max(0, gross - fees), fees, changed: Math.abs(gross - round2(l.formulaGross)) > 0.005 };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, edits],
  );
  const totalGross = rows.reduce((s, r) => s + r.gross, 0);
  const totalFormula = lines.reduce((s, l) => s + l.formulaGross, 0);

  function setRow(k: string, patch: Partial<RowEdit>) {
    setEdits((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));
  }
  function resetRow(l: CommissionEditLine) {
    setEdits((prev) => ({ ...prev, [keyOf(l)]: l.formulaRate > 0
      ? { mode: "percent_rate", pct: String(+(l.formulaRate * 100).toFixed(3)), flat: "" }
      : { mode: "flat_amount", pct: "", flat: "" } }));
  }

  async function save() {
    if (reason.trim().length < 5) { setError("Reason required (min 5 chars)."); return; }
    if (!member?.id) { setError("Team roster row not found."); return; }
    setSaving(true);
    setError(null);
    try {
      const overrides: Record<string, number> = {};
      for (const r of rows) if (r.changed) overrides[keyOf(r.l)] = r.gross;
      const payload = Object.keys(overrides).length ? overrides : null;

      const { error: upErr } = await supabase
        .from("deals")
        .update({ commission_line_overrides: payload, rm_commission: null })
        .eq("id", deal.id);
      if (upErr) throw upErr;

      const itemized = rows.map((r) =>
        `${r.l.name} (${r.l.role}): ${fmtUSD(r.gross)}${r.changed ? ` — was ${fmtUSD(r.l.formulaGross)} by formula` : " — formula"}`,
      ).join("\n");
      const { error: noteErr } = await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        body: `Commission edited by ${member.full_name}.\n\n${itemized}\n\nReason: ${reason.trim()}`,
        kind: "commission_edit",
        created_by: member.id,
      });
      if (noteErr) console.warn("commission edit note failed", noteErr);
      const { error: auditErr } = await supabase.from("finance_audit_log").insert({
        deal_id: deal.id,
        actor_id: member.id,
        action: "commission_edit",
        is_financial: true,
        amount_delta: round2(totalGross - totalFormula),
        before_jsonb: {
          commission_line_overrides: deal.commission_line_overrides ?? null,
          rm_commission: deal.rm_commission ?? null,
          formula: lines.map((l) => ({ member_id: l.personId, role: l.role, gross: round2(l.formulaGross), base: round2(l.base), rate: l.formulaRate })),
        },
        after_jsonb: {
          source: "commission_edit_modal_v2",
          commission_line_overrides: payload,
          lines: rows.map((r) => ({ member_id: r.l.personId, role: r.l.role, gross: r.gross, changed: r.changed })),
        },
        reason: reason.trim(),
      });
      if (auditErr) console.warn("commission edit audit failed", auditErr);

      onSaved();
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-bardin-ink">
            Edit commission — {deal.homeowner_name ?? "(no name)"}
          </h2>
          <p className="text-xs text-slate-500">
            #{deal.claim_number ?? "—"} · {isRetail ? "retail pool" : "revenue"} {fmtUSD(revenue)}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="text-xs bg-blue-50 border border-blue-100 rounded p-3 text-blue-700">
            ⓘ Each row defaults to what the formula pays that person: RMs at their own rate on their own half,
            TL 1% on the half their RM reports to them, ASM 4% of revenue excluding their own half
            {hasUpsideRows ? ", the trade upside pool to the RM(s)" : ""}. A % is a %
            of that row's base. Only the rows you change are saved; the reason posts to the deal's notes.
          </div>

          {isRetail && (
            <div className="text-xs bg-teal-50 border border-teal-300 rounded p-3 text-teal-900">
              🏷 Retail — the pool goes to the RM until you split it. Give TL/ASM a flat $ share and lower the RM
              row by the same amount so the total stays at the pool ({fmtUSD(revenue)}).
            </div>
          )}

          {hasUpsideRows && (
            <div className="text-xs bg-teal-50 border border-teal-300 rounded p-3 text-teal-900">
              🔧 Trade upside pool {fmtUSD(upsidePool)} — the homeowner's price above the 30% floor on the trades billed to
              them. It goes to the RM until you split it{deal.split_rm_id ? " (half each on this split)" : ""}: give the TL / ASM
              a flat $ share on their Upside row and lower the RM's Upside row by the same amount so the Upside rows still
              total {fmtUSD(upsidePool)}. No deduction touches the pool.
            </div>
          )}

          {rows.length === 0 && (
            <div className="text-sm text-slate-500">No commission lines on this deal (no RM assigned).</div>
          )}

          {rows.map(({ l, gross, net, fees, changed }) => {
            const k = keyOf(l);
            const e = edits[k];
            const share = revenue > 0 ? l.base / revenue : 1;
            const poolShare = l.kind === "upside" && upsidePool > 0 ? l.base / upsidePool : 1;
            const baseNote = l.kind === "upside"
              ? poolShare > 0.999 ? "— the trade upside pool" : poolShare > 0.499 && poolShare < 0.501 ? "— ½ of the trade upside pool" : `— ${Math.round(poolShare * 100)}% of the trade upside pool`
              : isRetail
              ? "of the pool"
              : share > 0.999 ? "of revenue" : share > 0.499 && share < 0.501 ? "— ½ of revenue" : `— ${Math.round(share * 100)}% of revenue`;
            return (
              <div key={k} className={"border rounded-lg p-3 " + (changed ? "border-amber-300 bg-amber-50/40" : "border-slate-200")}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${ROLE_CLS[l.role] ?? ""}`}>{l.role}</span>
                      <span className="text-sm font-semibold text-bardin-ink">{l.name}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Base {fmtUSD(l.base)} {baseNote}
                      {l.formulaRate > 0
                        ? <> · formula {+(l.formulaRate * 100).toFixed(2)}% = {fmtUSD(l.formulaGross)}</>
                        : <> · formula {fmtUSD(l.formulaGross)}</>}
                    </div>
                  </div>
                  <select
                    value={e.mode}
                    onChange={(ev) => {
                      const mode = ev.target.value as RowEdit["mode"];
                      setRow(k, mode === "flat_amount" ? { mode, flat: gross.toFixed(2) } : { mode, pct: l.base > 0 ? String(+((gross / l.base) * 100).toFixed(3)) : "0" });
                    }}
                    className="text-xs border border-slate-300 rounded px-2 py-1"
                  >
                    <option value="percent_rate">% of base</option>
                    <option value="flat_amount">Flat $</option>
                  </select>
                  {e.mode === "percent_rate" ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number" step="0.5" min="0"
                        value={e.pct}
                        onChange={(ev) => setRow(k, { pct: ev.target.value })}
                        className="w-20 text-sm border border-slate-300 rounded px-2 py-1 text-right"
                      />
                      <span className="text-sm">%</span>
                    </div>
                  ) : (
                    <input
                      type="number" step="0.01" min="0"
                      value={e.flat}
                      onChange={(ev) => setRow(k, { flat: ev.target.value })}
                      placeholder="0.00"
                      className="w-28 text-sm border border-slate-300 rounded px-2 py-1 text-right"
                    />
                  )}
                  <div className="text-right w-28">
                    <div className="text-sm font-semibold text-bardin-orange">→ {fmtUSD(gross)}</div>
                    {fees > 0.005 && <div className="text-[11px] text-rose-600">pays {fmtUSD(net)} after −{fmtUSD(fees)}</div>}
                  </div>
                </div>
                {changed && (
                  <button onClick={() => resetRow(l)} className="mt-1 text-[11px] text-slate-500 hover:text-slate-700 underline">
                    Reset to formula ({fmtUSD(l.formulaGross)})
                  </button>
                )}
              </div>
            );
          })}

          {rows.length > 0 && (
            <div className="flex items-center justify-between text-xs px-1 text-slate-600">
              <span>Total commission (gross)</span>
              <span className="font-semibold">
                {fmtUSD(totalGross)}
                {Math.abs(totalGross - totalFormula) > 0.005 && (
                  <span className="ml-1 font-normal text-slate-500">(formula {fmtUSD(totalFormula)})</span>
                )}
              </span>
            </div>
          )}

          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              Reason for change <span className="text-red-600">(required)</span>
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this commission different from the standard? The reps see it in the deal's notes."
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Posts to the deal's notes · also written to the Finance Audit Log. This deal only — per-person
              rates are set on the Team Roster board.
            </div>
          </div>

          {error && <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">⚠ {error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={saving} className="px-4 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
          <button
            onClick={save}
            disabled={saving || reason.trim().length < 5 || rows.length === 0}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save change"}
          </button>
        </div>
      </div>
    </div>
  );
}
